function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty(target, key, source[key]); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import MaterialTypography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import withStyles from '@material-ui/core/styles/withStyles';
/**
 * The `<Typography>` component contains a block of text which is styled
 * according to the current theme.
 *
 * ~~~js
 * import { Typography } from '@theconversation/ui'
 *
 * <Typography variant='body1'>
 *   lorem ipsum
 * </Typography>
 * ~~~
 */
// Default overrides which prevent TC's legacy top level styles from having
// a negative impact on typography components.
//
// Note that some properties have been skipped intentionally, such as margin.
//
// The margin is set via a different class on a Typography by MUI itself, which
// is already specific enough to override TC's styles. Including a reset
// for those properties (margin, color, etc) would cause more harm than good.

var LEGACY_HEADING_RESETS = {
  fontSize: 'initial',
  fontWeight: 'initial',
  textTransform: 'initial',
  letterSpacing: 'initial'
};

var styles = function styles(theme) {
  var _body, _body2;

  return {
    h1: _objectSpread({}, LEGACY_HEADING_RESETS, _defineProperty({
      fontFamily: 'Montserrat',
      fontWeight: 700,
      fontSize: '1.75rem'
    }, theme.breakpoints.up('sm'), {
      fontSize: '2.375rem'
    })),
    h2: _objectSpread({}, LEGACY_HEADING_RESETS, _defineProperty({
      fontFamily: 'Montserrat',
      fontWeight: 700,
      fontSize: '1.625rem'
    }, theme.breakpoints.up('sm'), {
      fontSize: '2rem'
    })),
    h3: _objectSpread({}, LEGACY_HEADING_RESETS, {
      fontFamily: 'Montserrat',
      fontWeight: 700,
      fontSize: '1.375rem'
    }),
    h4: _objectSpread({}, LEGACY_HEADING_RESETS, {
      fontFamily: 'Montserrat',
      fontWeight: 600,
      fontSize: '1.25rem'
    }),
    h5: _objectSpread({}, LEGACY_HEADING_RESETS, {
      fontFamily: 'Montserrat',
      fontWeight: 600,
      fontSize: '1.125rem'
    }),
    h6: _objectSpread({}, LEGACY_HEADING_RESETS, {
      fontFamily: 'Montserrat',
      fontWeight: 700,
      lineHeight: 'normal',
      letterSpacing: '0.25px',
      fontSize: '1rem'
    }),
    body1: (_body = {
      fontFamily: 'Libre Baskerville',
      lineHeight: '1.6',
      fontSize: '1rem'
    }, _defineProperty(_body, theme.breakpoints.up('sm'), {
      fontSize: '1.125rem'
    }), _defineProperty(_body, "whiteSpace", 'pre-line'), _body),
    body2: (_body2 = {
      fontFamily: 'Noto Sans',
      lineHeight: '1.5',
      fontSize: '1.125rem'
    }, _defineProperty(_body2, theme.breakpoints.up('sm'), {
      fontSize: '1.5rem'
    }), _defineProperty(_body2, "whiteSpace", 'pre-line'), _body2),
    button: _defineProperty({
      textTransform: 'none',
      fontSize: '0.875rem'
    }, theme.breakpoints.up('sm'), {
      fontSize: '1rem'
    }),
    overline: _defineProperty({
      letterSpacing: 0.25,
      textTransform: 'none',
      fontSize: '0.875rem'
    }, theme.breakpoints.up('sm'), {
      fontSize: '1rem'
    }),
    caption: {
      fontSize: '0.75rem'
    },
    subtitle1: {
      fontSize: '1rem',
      lineHeight: '1.3'
    },
    subtitle2: {
      fontSize: '0.75rem'
    }
  };
};

export var Typography = function Typography(_ref) {
  var children = _ref.children,
      colour = _ref.colour,
      other = _objectWithoutProperties(_ref, ["children", "colour"]);

  return React.createElement(MaterialTypography, _extends({
    color: colour
  }, other), children);
};
Typography.propTypes = {
  /**
   * The alignment of the text.
   */
  align: PropTypes.oneOf(['left', 'center', 'right', 'justify', 'inherit']),

  /**
   * The content of the text.
   */
  children: PropTypes.node.isRequired,

  /**
   * Overrides the styles applied to the component.
   */
  classes: PropTypes.object,

  /**
   * The colour of the text.
   */
  colour: PropTypes.oneOf(['default', 'primary', 'secondary', 'error', 'inherit']),

  /**
   * If `true`, the text will have a bottom margin.
   */
  gutterBottom: PropTypes.bool,

  /**
   * If `true`, the text will not wrap, but instead will be truncated with an
   * ellipsis.
   */
  noWrap: PropTypes.bool,

  /**
   * If `true`, the text will have a bottom margin.
   */
  paragraph: PropTypes.bool,

  /**
   * The variant of the text styling.
   */
  variant: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'subtitle1', 'subtitle2', 'body1', 'body2', 'button', 'caption', 'overline', 'inherit'])
};
Typography.defaultProps = {
  align: 'inherit',
  colour: 'inherit',
  gutterBottom: false,
  noWrap: false,
  paragraph: false,
  variant: 'inherit'
};
export default withStyles(styles)(Typography);